import { Dialog, DialogContent, DialogHeader, DialogTitle } from "src/@/components/ui/dialog";
import { Input } from "src/@/components/ui/input";
import { Button } from "src/@/components/ui/button";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useAuth from "src/hooks/useAuth";
import v1_actionWboTemplates from "src/actions/v1/meta/wbo/v1_actionWboTemplates";
import v2_actionMetaWhatsappEnviarTemplate from "src/actions/v2/meta/wbo/v2_actionMetaWhatsappEnviarTemplate";
import { transformCategoryTemplateWhatsApp, transformStatusTemplateWhatsApp } from "src/utils/meta.utils";
import { Send, Search, FileText, Check, X } from "lucide-react";

// Design System Constants
const BRAND_COLOR = "#7C3AED";
const BRAND_BG_SUBTLE = "bg-[#7C3AED]/5";
const BRAND_BG_LIGHT = "bg-[#7C3AED]/10";
const BRAND_BORDER = "border-[#7C3AED]";
const BRAND_TEXT = "text-[#7C3AED]";
const DIALOG_RADIUS = "rounded-2xl";
const CARD_RADIUS = "rounded-xl";

export default function ModalTemplatesWhatsApp({
  dialogTemplates,
  onClose,
  onSelect,
  chat, // Adicionar chat como prop para obter o número do cliente
  modoAutomacao = false, // Modo para automações - retorna template completo
}) {
  const { t } = useTranslation();
  // estado para templates do WhatsApp
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [pesquisaTemplate, setPesquisaTemplate] = useState("");
  const [templateSelecionado, setTemplateSelecionado] = useState(null);
  const [variaveisTemplate, setVariaveisTemplate] = useState({});
  const [mostrarCamposVariaveis, setMostrarCamposVariaveis] = useState(false);
  const [enviandoTemplate, setEnviandoTemplate] = useState(false);
  const [previewTexto, setPreviewTexto] = useState('');
  const [positions, setPositions] = useState([]);
  const [erroEnvio, setErroEnvio] = useState('');
  const [variaveisBotoes, setVariaveisBotoes] = useState([]); // [{buttonIndex, varNum, value}]

  // Normaliza os tipos dos components (body/footer/buttons => maiúsculo)
  const normalizarComponents = (components) => {
    if (!Array.isArray(components)) return [];
    return components.map((c) => ({
      ...c,
      type: typeof c.type === 'string' ? c.type.toUpperCase() : c.type,
    }));
  };

  // Validação: exige categoria, nome e corpo do template (BODY)
  const validarTemplateBasico = (tpl) => {
    if (!tpl) return false;
    const comps = normalizarComponents(tpl.components);
    const bodyText = comps?.find((c) => c.type === 'BODY')?.text || '';
    const hasBody = Boolean(String(bodyText || '').trim());
    const hasName = Boolean(tpl?.templateName || tpl?.name);
    const hasCategory = Boolean(tpl?.category);
    return hasBody && hasName && hasCategory;
  };

  // Função para extrair variáveis {{value}} dos componentes
  const extrairVariaveisTemplate = (components) => {
    const variaveis = {};

    if (!components || !Array.isArray(components)) {
      return variaveis;
    }

    components.forEach((component) => {
      // Verificar texto do componente
      if (component.text) {
        // Buscar padrões como {{1}}, {{2}}, etc.
        const matches = component.text.match(/\{\{(\d+)\}\}/g);
        if (matches) {
          matches.forEach((match) => {
            const numero = match.replace(/\{\{|\}\}/g, '');
            variaveis[numero] = '';
          });
        }
        // Também procurar sem as chaves duplas (caso o formato seja diferente)
        const matchesAlt = component.text.match(/\{(\d+)\}/g);
        if (matchesAlt && !matches) {
          matchesAlt.forEach((match) => {
            const numero = match.replace(/\{|\}/g, '');
            variaveis[numero] = '';
          });
        }
      }

      // Verificar botões se existirem
      if (component.buttons && Array.isArray(component.buttons)) {
        component.buttons.forEach((button) => {
          if (button.text) {
            const matches = button.text.match(/\{\{(\d+)\}\}/g);
            if (matches) {
              matches.forEach((match) => {
                const numero = match.replace(/\{\{|\}\}/g, '');
                variaveis[numero] = '';
              });
            }
          }
        });
      }
    });

    return variaveis;
  };

  // Extrair variáveis de URLs dos botões (separado pois vão ao final do positions)
  const extrairVariaveisBotoesUrl = (components) => {
    const vars = [];
    if (!components || !Array.isArray(components)) return vars;

    components.forEach((component) => {
      if (component.buttons && Array.isArray(component.buttons)) {
        component.buttons.forEach((button, btnIndex) => {
          if (button.url) {
            const matches = button.url.match(/\{\{(\d+)\}\}/g);
            if (matches) {
              matches.forEach((match) => {
                const numero = match.replace(/\{\{|\}\}/g, '');
                vars.push({ buttonIndex: btnIndex, varNum: numero, value: '', buttonText: button.text || t('chat.templates.button', 'Botão') });
              });
            }
          }
        });
      }
    });

    return vars;
  };

  // Função para substituir variáveis no texto
  const substituirVariaveis = (texto, variaveis) => {
    if (!texto || !variaveis) return texto;

    let textoSubstituido = texto;
    Object.keys(variaveis).forEach((key) => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      textoSubstituido = textoSubstituido.replace(regex, variaveis[key] || `{{${key}}}`);
    });

    return textoSubstituido;
  };

  // Função para gerar preview visual do template
  const gerarPreviewTemplate = (texto, variaveis) => {
    if (!texto) return '';

    let preview = texto;
    Object.keys(variaveis).forEach((key) => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      const valor = variaveis[key] || '';
      preview = preview.replace(regex, valor);
    });

    return preview;
  };

  // Função para criar campos editáveis inline no preview
  const criarPreviewComCampos = (texto, variaveis) => {
    if (!texto) return null;

    const partes = texto.split(/(\{\{\d+\}\})/g);

    return partes.map((parte, index) => {
      const match = parte.match(/\{\{(\d+)\}\}/);
      if (match) {
        const numeroVariavel = match[1];
        return (
          <span key={index} className="inline-block">
            <input
              type="text"
              value={variaveis[numeroVariavel] || ''}
              onChange={(e) => handleAtualizarVariavel(numeroVariavel, e.target.value)}
              placeholder={`{{${numeroVariavel}}}`}
              className={`inline-block min-w-[60px] px-2 py-0.5 mx-1 border border-[#7C3AED]/30 rounded-lg ${BRAND_BG_SUBTLE} text-gray-800 text-sm focus:outline-none focus:ring-1 focus:ring-[#7C3AED] focus:border-[#7C3AED] transition-all`}
            />
          </span>
        );
      }
      return <span key={index}>{parte}</span>;
    });
  };

  const buscarTemplates = async () => {
    if (!chat?.cliente_id) return;
    try {
      setLoadingTemplates(true);
      console.log('Buscando templates para cliente_id:', chat.cliente_id);
      const res = await v1_actionWboTemplates(chat.cliente_id);
      setTemplates(res.data?.data?.templates);
    } catch (error) {
      // silencia erro no modal
      // console.log(error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  useEffect(() => {
    if (dialogTemplates) {
      buscarTemplates();
    }
  }, [dialogTemplates]);

  const handleSelecionarTemplate = (template) => {
    const comps = normalizarComponents(template?.components);
    const conteudo = comps?.find(c => c.type === 'BODY')?.text || '';
    // Extrair variáveis dos componentes normalizados
    const variaveis = extrairVariaveisTemplate(comps);
    const varsBotoes = extrairVariaveisBotoesUrl(comps);
    const temVariaveis = Object.keys(variaveis).length > 0 || varsBotoes.length > 0;

    setTemplateSelecionado({
      ...template,
      conteudo: conteudo
    });

    setVariaveisTemplate(variaveis);
    setVariaveisBotoes(varsBotoes);
    setMostrarCamposVariaveis(temVariaveis);

    // Inicializar positions: texto vars em ordem + botão URL vars
    const variaveisOrdenadas = Object.keys(variaveis).sort((a, b) => parseInt(a) - parseInt(b));
    const posTexto = variaveisOrdenadas.map(key => variaveis[key] || '');
    const posBotoes = varsBotoes.map(v => v.value || '');
    setPositions([...posTexto, ...posBotoes]);

    // Gerar preview inicial
    const previewInicial = gerarPreviewTemplate(conteudo, variaveis);
    setPreviewTexto(previewInicial);
  };

  const handleConfirmarEnvio = async (enviarDireto = false) => {
    if (templateSelecionado) {
      // Bloqueia quando faltar categoria, nome ou corpo
      if (!validarTemplateBasico(templateSelecionado)) {
        setErroEnvio(t('chat.templates.errors.validation'));
        return;
      }

      // Substituir variáveis no conteúdo
      let conteudoFinal = templateSelecionado.conteudo;
      if (Object.keys(variaveisTemplate).length > 0) {
        conteudoFinal = substituirVariaveis(templateSelecionado.conteudo, variaveisTemplate);
      }

      if (enviarDireto && chat?.numeroCliente) {
        // Enviar template diretamente via API
        setEnviandoTemplate(true);
        try {
          setErroEnvio('');
          const querys = `?sessao=${chat.sessao}&canal=${chat.canal}`;
          const data = {
            phone: chat.numeroCliente,
            templateId: templateSelecionado.templateId
          };

          // Se há variáveis (texto ou botão URL), adicionar os parâmetros em ordem
          if (Object.keys(variaveisTemplate).length > 0 || variaveisBotoes.length > 0) {
            data.positions = positions;
          }

          await v2_actionMetaWhatsappEnviarTemplate(querys, data);

          // Limpar estados e fechar modal
          setTemplateSelecionado(null);
          setVariaveisTemplate({});
          setVariaveisBotoes([]);
          setMostrarCamposVariaveis(false);
          setPreviewTexto('');
          setPositions([]);
          setErroEnvio('');
          onClose();
        } catch (error) {
          console.error('Erro ao enviar template:', error);
          const mensagem = (error && (error?.response?.data?.message || error?.response?.data?.msg)) ? String(error?.response?.data?.message || error?.response?.data?.msg) : t('chat.templates.errors.tryAgain');
          setErroEnvio(`${mensagem}`);
        } finally {
          setEnviandoTemplate(false);
        }
      } else {
        if (modoAutomacao) {
          // Modo automação - retornar template completo com variáveis
          onSelect({
            template: templateSelecionado,
            variaveis: variaveisTemplate,
            positions: positions
          });
        } else {
          // Comportamento original - apenas selecionar para edição
          onSelect({
            tipo: "texto",
            titulo: templateSelecionado.name,
            resposta: conteudoFinal
          });
        }
        setTemplateSelecionado(null);
        setVariaveisTemplate({});
        setVariaveisBotoes([]);
        setMostrarCamposVariaveis(false);
        setPreviewTexto('');
        setPositions([]);
        onClose();
      }
    }
  };

  const handleCancelarSelecao = () => {
    setTemplateSelecionado(null);
    setVariaveisTemplate({});
    setVariaveisBotoes([]);
    setMostrarCamposVariaveis(false);
    setPreviewTexto('');
    setPositions([]);
  };

  const handleAtualizarVariavel = (variavel, valor) => {
    const novasVariaveis = { ...variaveisTemplate, [variavel]: valor };
    setVariaveisTemplate(novasVariaveis);

    // Reconstruir positions: texto vars + botão URL vars
    const variaveisOrdenadas = Object.keys(novasVariaveis).sort((a, b) => parseInt(a) - parseInt(b));
    const posTexto = variaveisOrdenadas.map(key => novasVariaveis[key] || '');
    const posBotoes = variaveisBotoes.map(v => v.value || '');
    setPositions([...posTexto, ...posBotoes]);

    // Atualizar preview em tempo real
    const novoPreview = gerarPreviewTemplate(templateSelecionado.conteudo, novasVariaveis);
    setPreviewTexto(novoPreview);
  };

  const handleAtualizarVariavelBotao = (index, valor) => {
    setVariaveisBotoes(prev => {
      const novos = [...prev];
      novos[index] = { ...novos[index], value: valor };

      // Reconstruir positions: texto vars + botão URL vars
      const variaveisOrdenadas = Object.keys(variaveisTemplate).sort((a, b) => parseInt(a) - parseInt(b));
      const posTexto = variaveisOrdenadas.map(key => variaveisTemplate[key] || '');
      const posBotoes = novos.map(v => v.value || '');
      setPositions([...posTexto, ...posBotoes]);

      return novos;
    });
  };

  const listaTemplatesFiltrada = (templates || []).filter((tpl) => {
    const termo = pesquisaTemplate.toLowerCase();
    const conteudo = normalizarComponents(tpl?.components)?.find(c => c.type === 'BODY')?.text || '';
    return (
      tpl?.name?.toLowerCase().includes(termo) ||
      transformCategoryTemplateWhatsApp(tpl?.category)?.toLowerCase().includes(termo) ||
      conteudo.toLowerCase().includes(termo)
    );
  });

  return (
    <Dialog open={dialogTemplates} onOpenChange={onClose}>
      <DialogContent className={`max-w-7xl w-[90vw] h-[85vh] flex flex-col p-0 gap-0 border-0 ${DIALOG_RADIUS} overflow-hidden shadow-2xl`}>
        <DialogHeader className="flex-shrink-0 px-6 py-4 border-b border-gray-100 bg-white/80 dark:bg-none backdrop-blur-sm sticky top-0 z-10">
          <DialogTitle className="text-lg font-semibold flex items-center gap-2.5 text-gray-800 justify-between">
            <div className="flex items-center gap-2 flex-row">
              <div className={`p-1.5 rounded-lg ${BRAND_BG_SUBTLE} ${BRAND_TEXT}`}>
                <FileText className="w-5 h-5" />
              </div>
              {t('chat.templates.titleBusiness')}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={onClose}>
                {t('common.buttons.close')}
              </Button>
            </div>
          </DialogTitle>

        </DialogHeader>

        <div className="flex-1 flex min-h-0 bg-white">
          {/* Coluna da esquerda - Lista de templates */}
          <div className="w-[400px] flex flex-col border-r border-gray-100 bg-gray-50/50">
            {/* Barra de pesquisa */}
            <div className="flex-shrink-0 p-4 pb-2">
              <div className="relative">
                <Input
                  value={pesquisaTemplate}
                  onChange={(e) => setPesquisaTemplate(e.target.value)}
                  placeholder={t('chat.templates.searchByNameOrContent')}
                  className="w-full pl-10 pr-4 py-2 text-sm bg-white border-gray-200 rounded-xl focus:ring-[#7C3AED] focus:border-[#7C3AED] shadow-sm"
                />
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              </div>
            </div>

            {/* Lista de templates */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
              {loadingTemplates ? (
                <div className="flex flex-col items-center justify-center h-40 gap-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#7C3AED]"></div>
                  <p className="text-sm text-gray-500">{t('chat.templates.loading')}</p>
                </div>
              ) : listaTemplatesFiltrada.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-center p-4">
                  <div className="text-4xl mb-3 grayscale opacity-30">📭</div>
                  <p className="text-sm text-gray-500">
                    {pesquisaTemplate ? t('chat.templates.noSearchResults') : t('chat.templates.empty')}
                  </p>
                </div>
              ) : (
                listaTemplatesFiltrada.map((tpl, index) => {
                  const isSelected = templateSelecionado?._id === tpl?._id;

                  return (
                    <div
                      key={tpl?._id || index}
                      className={`group relative p-3.5 cursor-pointer border transition-all duration-200 ${CARD_RADIUS} ${isSelected
                        ? `${BRAND_BG_SUBTLE} ${BRAND_BORDER} ring-1 ring-[#7C3AED]/20 shadow-sm`
                        : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-xs'
                        }`}
                      onClick={() => handleSelecionarTemplate(tpl)}
                    >
                      <div className="flex justify-between items-start mb-1.5">
                        <h3 className={`font-medium text-sm truncate pr-2 ${isSelected ? 'text-[#7C3AED]' : 'text-gray-900'}`}>
                          {tpl?.templateName || t('chat.templates.unnamed')}
                        </h3>
                        {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-[#7C3AED]" />}
                      </div>

                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] uppercase font-bold tracking-wider text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-md">
                          {transformCategoryTemplateWhatsApp(tpl?.category) || t('chat.templates.categoryGeneral')}
                        </span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-medium ${tpl?.status === 'APPROVED' ? 'bg-green-50 text-green-700' :
                          tpl?.status === 'PENDING' ? 'bg-yellow-50 text-yellow-700' :
                            'bg-gray-100 text-gray-500'
                          }`}>
                          {transformStatusTemplateWhatsApp(tpl?.status)}
                        </span>
                      </div>

                      <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
                        {normalizarComponents(tpl?.components)?.find(c => c.type === 'BODY')?.text || ''}
                      </p>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Coluna da direita - Preview */}
          <div className="flex-1 flex flex-col bg-white overflow-hidden relative">
            {/* Background Decorativo */}
            <div className="absolute inset-0 opacity-40 pointer-events-none"
              style={{
                backgroundImage: "url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')",
                backgroundRepeat: "repeat"
              }}
            />

            {!templateSelecionado ? (
              <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-8 z-10">
                <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mb-4 shadow-sm border border-gray-100">
                  <Search className="w-8 h-8 opacity-20" />
                </div>
                <p className="text-sm font-medium">{t('chat.templates.selectToPreview')}</p>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto p-8 z-10 custom-scrollbar">
                  <div className="max-w-md mx-auto">
                    <div className="bg-white rounded-tr-2xl rounded-tl-2xl rounded-br-2xl rounded-bl-none shadow-sm border border-gray-100 p-4 mb-2 relative">
                      {/* Body */}
                      <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                        {mostrarCamposVariaveis ? (
                          <div className="space-y-1">
                            {criarPreviewComCampos(templateSelecionado.conteudo, variaveisTemplate)}
                          </div>
                        ) : (
                          <span>{previewTexto || templateSelecionado.conteudo}</span>
                        )}
                      </div>

                      {/* Metadata */}
                      <div className="flex justify-end items-center gap-1 mt-2">
                        <span className="text-[10px] text-gray-400">
                          {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>

                    {/* Footer Buttons Preview */}
                    {(() => {
                      const comps = normalizarComponents(templateSelecionado?.components);
                      const buttonsComp = comps?.find(c => c.type === 'BUTTONS');
                      if (!buttonsComp?.buttons?.length) return null;
                      return (
                        <div className="space-y-1.5 mt-2">
                          {buttonsComp.buttons.map((btn, i) => (
                            <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 text-center py-2.5 text-blue-500 text-sm font-medium cursor-default">
                              {btn.text || t('chat.templates.button')}
                            </div>
                          ))}
                        </div>
                      );
                    })()}

                    {/* Campos para variáveis de URL dos botões */}
                    {variaveisBotoes.length > 0 && (
                      <div className="mt-4 bg-white/90 rounded-xl border border-gray-200 p-4 space-y-3">
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                          {t('chat.templates.buttonVariables')}
                        </p>
                        {variaveisBotoes.map((v, i) => (
                          <div key={i}>
                            <label className="text-xs text-gray-500 mb-1 block">
                              {v.buttonText} - {t('chat.templates.parameter')} {`{{${v.varNum}}}`}
                            </label>
                            <input
                              type="text"
                              value={v.value}
                              onChange={(e) => handleAtualizarVariavelBotao(i, e.target.value)}
                              placeholder={t('chat.templates.buttonVariablePlaceholder', { num: v.varNum })}
                              className={`w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-[#7C3AED] focus:border-[#7C3AED] transition-all`}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer Action Bar */}
                <div className="p-4 border-t border-gray-100 bg-white/95 dark:bg-none backdrop-blur z-20">
                  <div className="flex items-center justify-between max-w-4xl mx-auto gap-4">
                    {erroEnvio && (
                      <div className="absolute bottom-full mb-4 left-1/2 -translate-x-1/2 bg-red-50 text-red-600 px-4 py-2 rounded-lg text-sm border border-red-100 shadow-sm animate-in fade-in slide-in-from-bottom-2">
                        {erroEnvio}
                      </div>
                    )}

                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      {mostrarCamposVariaveis && (
                        <span className="flex items-center gap-2 animate-pulse">
                          <span className="w-2 h-2 rounded-full bg-[#7C3AED]"></span>
                          {t('chat.templates.fillVariablesHint')}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-3">
                      <Button
                        variant="ghost"
                        onClick={handleCancelarSelecao}
                        className="text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                      >
                        {t('common.buttons.cancel')}
                      </Button>

                      <Button
                        onClick={() => handleConfirmarEnvio(modoAutomacao ? false : true)}
                        disabled={
                          (!modoAutomacao && mostrarCamposVariaveis && (
                            Object.values(variaveisTemplate).some(valor => !String(valor || '').trim()) ||
                            variaveisBotoes.some(v => !String(v.value || '').trim())
                          )) ||
                          enviandoTemplate ||
                          !validarTemplateBasico(templateSelecionado)
                        }
                        className={`h-10 px-6 rounded-xl font-medium shadow-sm transition-all text-white
                             ${enviandoTemplate ? 'opacity-70 cursor-not-allowed' : ''}
                           `}
                        style={{ backgroundColor: BRAND_COLOR }}
                      >
                        {enviandoTemplate ? (
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            <span>{t('chat.templates.sending')}</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Send className="w-4 h-4" />
                            <span>
                              {modoAutomacao
                                ? t('chat.templates.useTemplate')
                                : t('chat.templates.sendMessage')
                              }
                            </span>
                          </div>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
